
import { useState, useEffect } from 'react';
import { DogSchool } from '@/data/dogSchools';
import { fetchDogSchoolsByPostalCode } from '@/services/postal/postalService';
import { fetchDogSchoolsByCountry, fetchDogSchoolsByState, fetchPaginatedDogSchools } from '@/services/dogSchoolService';
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';

interface UseSchoolSearchProps {
  initialCountryName?: string;
  initialStateName?: string;
}

export const useSchoolSearch = ({ initialCountryName, initialStateName }: UseSchoolSearchProps = {}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  const [filteredSchools, setFilteredSchools] = useState<DogSchool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPostalCode, setUserPostalCode] = useState('');
  const [searchLocation, setSearchLocation] = useState<string>('');
  const [schoolCount, setSchoolCount] = useState<number | undefined>(undefined);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [isPaginated, setIsPaginated] = useState(true); // Default to true for all pages
  const initialPageSize = 25; // Initial load of 25 schools
  const subsequentPageSize = 15; // Load 15 schools at a time after initial load

  // Process search params on component mount or when they change
  useEffect(() => {
    const cityParam = searchParams.get('city');
    const postalCodeParam = searchParams.get('postalCode');
    const radiusParam = searchParams.get('radius');
    const countryParam = searchParams.get('country');
    
    // Only trigger search if we have at least one search parameter
    if (cityParam || postalCodeParam) {
      const radius = radiusParam ? parseInt(radiusParam, 10) : 50; // Default to larger radius (50km)
      handleSearch(postalCodeParam || '', cityParam || '', radius);
    } else {
      // Initial loading is done by the parent component when country/state changes
      setIsLoading(false);
    }
  }, [searchParams]);

  const handleSearch = async (postalCode: string, city: string, radius: number) => {
    setIsSearching(true);
    setError(null);
    setUserPostalCode(postalCode);
    setIsPaginated(false); // Reset pagination when doing a new search
    setPage(0); // Reset page when doing a new search
    setFilteredSchools([]); // Clear previous results
    
    try {
      console.log(`Searching for schools with postal code: ${postalCode}, city: ${city}, radius: ${radius}km`);
      
      // Set the search location for dynamic page titles and meta descriptions
      const displayLocation = postalCode || city || '';
      setSearchLocation(displayLocation);
      
      // If no postal code is provided, and we have country/state, show all schools in that area
      if (!postalCode.trim() && !city.trim() && initialCountryName) {
        let schools: DogSchool[] = [];
        
        if (initialStateName) {
          console.log(`No postal code or city provided. Showing all schools in ${initialStateName}, ${initialCountryName}`);
          // Use paginated loading for states too
          console.log("Using paginated loading for state view");
          setIsPaginated(true);
          const result = await fetchPaginatedDogSchools(0, initialPageSize, { 
            country: initialCountryName,
            state: initialStateName 
          });
          schools = result.schools;
          setHasMore(result.hasMore);
          setTotalCount(result.totalCount);
        } else {
          console.log(`No postal code or city provided. Showing all schools in ${initialCountryName}`);
          // Use paginated loading for all countries
          console.log("Using paginated loading for country view");
          setIsPaginated(true);
          const result = await fetchPaginatedDogSchools(0, initialPageSize, { country: initialCountryName });
          schools = result.schools;
          setHasMore(result.hasMore);
          setTotalCount(result.totalCount);
        }
        
        if (schools.length === 0) {
          showNoSchoolsFoundToast(initialStateName || initialCountryName);
        } else {
          showSchoolsFoundToast(schools.length, initialStateName || initialCountryName);
        }
        
        setFilteredSchools(schools);
        setSchoolCount(isPaginated ? totalCount || schools.length : schools.length);
      } else {
        // Search by postal code, city, and radius
        console.log(`Executing search with postal code: ${postalCode}, city: ${city}, radius: ${radius}km`);
        const schools = await fetchDogSchoolsByPostalCode(postalCode, city, radius);
        
        if (schools.length === 0) {
          showNoSchoolsFoundToast(postalCode || city, radius);
        } else {
          showSchoolsFoundToast(schools.length, postalCode || city, radius);
        }
        
        setFilteredSchools(schools);
        setSchoolCount(schools.length);
        
        // Enable pagination for search results if we have many results
        if (schools.length > initialPageSize) {
          setIsPaginated(true);
          setHasMore(true); // Allow loading more for search results too
        }
      }
    } catch (error) {
      console.error("Error searching for dog schools:", error);
      setError("Failed to search for dog schools. Please try again.");
      
      toast({
        title: t('error'),
        description: t('errorSearching'),
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const loadMoreSchools = async () => {
    // Only load more if we're in paginated mode and there are more schools to load
    if (!isPaginated || !hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    const nextPage = page + 1;
    
    try {
      console.log(`Loading more schools for page ${nextPage} with pageSize ${subsequentPageSize}`);
      // Determine if we're loading by country or state
      let result;
      if (initialStateName && initialCountryName) {
        result = await fetchPaginatedDogSchools(nextPage, subsequentPageSize, { 
          country: initialCountryName,
          state: initialStateName 
        });
      } else if (initialCountryName) {
        result = await fetchPaginatedDogSchools(nextPage, subsequentPageSize, { 
          country: initialCountryName
        });
      } else if (userPostalCode || searchLocation) {
        // If this was a search by postal code/city, we already have all results
        // (pagination was handled by the backend), so we just simulate "no more results"
        setHasMore(false);
        setIsLoadingMore(false);
        return;
      } else {
        // Generic case - load more based on current page
        result = await fetchPaginatedDogSchools(nextPage, subsequentPageSize, {});
      }
      
      // Append new schools to existing ones
      setFilteredSchools(prevSchools => [...prevSchools, ...result.schools]);
      setHasMore(result.hasMore);
      setPage(nextPage);
      setTotalCount(result.totalCount);
      setSchoolCount(result.totalCount || (filteredSchools.length + result.schools.length));
    } catch (error) {
      console.error("Error loading more schools:", error);
      toast({
        title: t('error'),
        description: t('errorLoadingMoreSchools'),
        variant: "destructive",
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const showNoSchoolsFoundToast = (location: string, radius?: number) => {
    let description = radius 
      ? `${t('noSchoolsFound')} ${t('tryDifferentSearch')}`
      : `${t('noSchoolsFound')} ${t('tryDifferentSearch')}`;
    
    toast({
      title: t('noSchoolsFound'),
      description,
      variant: "destructive",
    });
  };

  const showSchoolsFoundToast = (count: number, location: string, radius?: number) => {
    let message = isPaginated && totalCount 
      ? `${count} ${t('schoolsLoaded')} (${t('total')}: ${totalCount})` 
      : `${count} ${t('schoolsLoaded')}`;
      
    toast({
      title: t('schoolsFound'),
      description: message,
      variant: "default",
    });
  };

  const handleCountUpdate = (count: number) => {
    // Only update count if we're not in paginated mode, otherwise use totalCount
    if (!isPaginated) {
      setSchoolCount(count);
    }
  };

  return {
    filteredSchools,
    isLoading,
    isSearching,
    error,
    userPostalCode,
    searchLocation,
    schoolCount,
    handleSearch,
    handleCountUpdate,
    // Pagination properties
    isPaginated,
    hasMore,
    isLoadingMore,
    loadMoreSchools,
    page,
    // Expose setter functions for parent components
    setFilteredSchools,
    setSchoolCount,
    setIsLoading,
    setError,
    setSearchLocation,
    setIsPaginated,
    setHasMore
  };
};
